/**
 * NumberedSteps — V01 "Numbered + Tinted" (handoff round 2 #3)
 *
 * Static, presentational. Orange-tinted circle numerals, dashed dividers,
 * sans titles + serif body. Inline styles to bypass dev CSS chunk caching.
 */

import * as React from 'react'

const ORANGE = '#ff9951'
const ORANGE_TINT_10 = 'rgba(255, 153, 81, 0.10)'
const GRAY_200 = '#e5e7eb'
const GRAY_700 = '#374151'
const GRAY_900 = '#111827'

const SYS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
const SERIF = 'Charter, "Source Serif Pro", Georgia, "Times New Roman", serif'

export interface NumberedStep {
  title: string
  body: string
}

export interface NumberedStepsProps {
  eyebrow?: string
  title: string
  steps: NumberedStep[]
  className?: string
}

export function NumberedSteps({ title, steps }: NumberedStepsProps) {
  return (
    <section className="ed-numbered-steps" style={{ margin: '32px 0' }}>
      {/* Suppress the inherited .ed-prose ol li::before counter so we don't double-render the index */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .ed-numbered-steps ol li::before { content: none !important; display: none !important; }
            .ed-numbered-steps ol li { padding-left: 0 !important; }
          `,
        }}
      />
      <h2
        style={{
          fontFamily: 'inherit',
          fontSize: 30,
          fontWeight: 900,
          letterSpacing: '-0.025em',
          color: GRAY_900,
          margin: '32px 0 12px 0',
          textWrap: 'balance' as never,
        }}
      >
        {title}
      </h2>

      <ol
        style={{
          listStyle: 'none',
          margin: '12px 0 0',
          padding: 0,
          borderTop: `1px dashed ${GRAY_200}`,
        }}
      >
        {steps.map((s, i) => (
          <li
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '40px 1fr',
              gap: 18,
              padding: '20px 0',
              borderBottom: `1px dashed ${GRAY_200}`,
              alignItems: 'center',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                background: ORANGE_TINT_10,
                color: ORANGE,
                fontFamily: SYS,
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: '0.04em',
                fontVariantNumeric: 'tabular-nums',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <div>
              <h3
                style={{
                  fontFamily: 'inherit',
                  fontSize: 20,
                  fontWeight: 800,
                  letterSpacing: '-0.015em',
                  color: GRAY_900,
                  margin: '0 0 6px',
                  lineHeight: 1.25,
                }}
              >
                {s.title}
              </h3>
              <p
                style={{
                  fontFamily: 'inherit',
                  fontSize: 17,
                  lineHeight: 1.65,
                  color: GRAY_700,
                  margin: 0,
                }}
              >
                {s.body}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}
